import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { MagnifyingGlass, User, SignOut, UploadSimple, Shield, List, X, MusicNotes, FilmStrip, ArrowRight, Star } from "@phosphor-icons/react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useCopy } from "../context/CopyContext";
import { api } from "../lib/api";
import SmartImg from "./SmartImg";
import { bgUrl } from "../lib/img";
import MegaPanel from "./MegaMenu";
import MobileMenu from "./MobileMenu";
import { EDITS_LIVE } from "../lib/features";
import { styleText } from "../lib/audioStyles";
import NotificationBell from "./NotificationBell";

const NAV = [
  { to: "/audios", label: "Audios", menu: "audios" },
  { to: "/scenepacks", label: "Scenepacks", menu: "scenepacks" },
  { to: "/edits", label: "Edits", menu: "edits", soon: !EDITS_LIVE },
  { to: "/aa-plus", label: "AA+" },
  { to: "/contact", label: "Contact" },
];

const SEARCH_PROMPTS = ["Looking for an audio?", "Looking for a scenepack?", "Looking for a creator?"];

/** Types a phrase out, pauses, deletes it, pauses, then moves to the next — loops forever. */
function useTypewriterPlaceholder(phrases, { typeSpeed = 60, deleteSpeed = 32, holdMs = 1600, gapMs = 450 } = {}) {
  const [text, setText] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const onPhone = window.matchMedia && window.matchMedia("(max-width: 767px)").matches; // the box isn't even shown
    if (prefersReduced || onPhone) {
      setText(phrases[0] || "");
      return undefined;
    }
    const current = phrases[phraseIndex % phrases.length] || "";
    let timeout;
    if (!deleting && text.length < current.length) {
      timeout = setTimeout(() => setText(current.slice(0, text.length + 1)), typeSpeed);
    } else if (!deleting && text.length === current.length) {
      timeout = setTimeout(() => setDeleting(true), holdMs);
    } else if (deleting && text.length > 0) {
      timeout = setTimeout(() => setText(current.slice(0, text.length - 1)), deleteSpeed);
    } else {
      timeout = setTimeout(() => {
        setDeleting(false);
        setPhraseIndex((i) => (i + 1) % phrases.length);
      }, gapMs);
    }
    return () => clearTimeout(timeout);
  }, [text, deleting, phraseIndex, phrases, typeSpeed, deleteSpeed, holdMs, gapMs]);

  return text;
}

// The typing animation lives in its own component: it changes state every ~40ms, and it must not
// re-render the whole navbar each time (or run at all on phones, where the search box is hidden).
function TypedPlaceholder() {
  const text = useTypewriterPlaceholder(SEARCH_PROMPTS);
  return <span>{text}</span>;
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useCopy();
  const [open, setOpen] = useState(false);
  const [focusMobileSearch, setFocusMobileSearch] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);

  // Hover menus under Audios / Scenepacks / Edits. A short close delay keeps the
  // panel from flickering while the pointer travels from the link into it.
  const [mega, setMega] = useState(null);
  const megaTimer = useRef(null);
  const location = useLocation();
  const openMega = (key) => { clearTimeout(megaTimer.current); setMega(key); };
  const closeMega = () => { clearTimeout(megaTimer.current); megaTimer.current = setTimeout(() => setMega(null), 140); };
  useEffect(() => { setMega(null); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!mega) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMega(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mega]);

  // Close the profile menu on outside click or Escape.
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    const onKey = (e) => { if (e.key === "Escape") setMenu(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menu]);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null); // {audios, movies, scenepacks, creators, people}
  const [showDrop, setShowDrop] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);

  // Debounced live search — fires on every keystroke after a 180ms pause.
  useEffect(() => {
    const q = search.trim();
    if (!q) { setResults(null); setSearchLoading(false); return; }
    setSearchLoading(true);
    const handle = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}&limit=5`)
        .then((r) => setResults(r.data))
        .catch(() => setResults(null))
        .finally(() => setSearchLoading(false));
    }, 180);
    return () => clearTimeout(handle);
  }, [search]);

  // Close dropdown on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDrop(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setShowDrop(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const goTo = (path) => {
    setShowDrop(false);
    setSearch("");
    navigate(path);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    const q = search.trim();
    setSearch("");
    setShowDrop(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const totalResults =
    (results?.audios?.length || 0) +
    (results?.movies?.length || 0) +
    (results?.scenepacks?.length || 0) +
    (results?.people?.length || 0) +
    (results?.creators?.length || 0);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="main-navbar" onMouseLeave={closeMega}>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 h-20 flex items-center justify-between gap-3 sm:gap-6">
        <Link to="/" data-testid="nav-home-logo" className="flex items-center">
          <Logo />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              onMouseEnter={() => (n.menu ? openMega(n.menu) : closeMega())}
              onFocus={() => (n.menu ? openMega(n.menu) : undefined)}
              className={({ isActive }) =>
                `font-display font-bold tracking-wider text-xs uppercase px-4 py-2 transition-all ${
                  isActive
                    ? "text-white border-b border-[#880015]"
                    : mega && mega === n.menu ? "text-white" : "text-white/60 hover:text-white"
                }`
              }
            >
              {n.label}
              {n.soon && <span className="ml-1.5 align-middle text-[8px] tracking-[0.2em] px-1.5 py-[3px] rounded-full bg-gradient-to-r from-[#880015] to-[#C2102E] text-white">SOON</span>}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3 flex-1 max-w-md hidden md:flex">
          <form onSubmit={submitSearch} className="relative flex-1" ref={searchRef} data-testid="nav-search-form">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none z-10" />
            <input
              data-testid="nav-search-input"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowDrop(true); }}
              onFocus={() => setShowDrop(true)}
              placeholder=""
              aria-label={t("nav.search_placeholder", "Search audios, scenepacks, creators...")}
              className="search-input text-sm py-2.5"
              autoComplete="off"
            />
            {!search && (
              <div className="absolute left-12 top-1/2 -translate-y-1/2 pointer-events-none flex items-center text-sm text-white/40 max-w-[calc(100%-70px)] overflow-hidden whitespace-nowrap">
                <TypedPlaceholder />
                <span className="typewriter-cursor ml-0.5" />
              </div>
            )}
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(""); setResults(null); }}
                aria-label="Clear search"
                data-testid="nav-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white p-1"
              >
                <X size={14} weight="bold" />
              </button>
            )}

            {showDrop && search.trim() && (
              <div
                data-testid="nav-search-dropdown"
                className="absolute left-0 right-0 top-[calc(100%+8px)] glass rounded-xl border border-white/10 shadow-2xl max-h-[70vh] overflow-y-auto z-[60] animate-in fade-in slide-in-from-top-1 duration-150"
              >
                {searchLoading && !results && (
                  <div className="px-4 py-6 text-xs font-mono text-white/40 text-center">Searching...</div>
                )}

                {results && totalResults === 0 && !searchLoading && (
                  <div className="px-4 py-6 text-xs font-mono text-white/40 text-center">
                    No matches for &ldquo;<span className="text-white/70">{search}</span>&rdquo;
                  </div>
                )}

                {results?.audios?.length > 0 && (
                  <SearchGroup label="Audios" icon={<MusicNotes size={11} weight="fill" />}>
                    {results.audios.slice(0, 4).map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => goTo(`/search?q=${encodeURIComponent(a.title)}`)}
                        data-testid={`nav-search-audio-${a.id}`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition group"
                      >
                        <div className="w-9 h-9 rounded-md bg-cover bg-center border border-white/10 flex-shrink-0 bg-[#232323]"
                          style={{ backgroundImage: bgUrl(a.cover_url, 96) }} />
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-bold text-sm truncate group-hover:text-[#FF8090] transition">{a.title}</div>
                          <div className="text-[11px] text-white/50 truncate">{a.creator_name || "Unknown"} · {styleText(a, " · ")}</div>
                        </div>
                      </button>
                    ))}
                  </SearchGroup>
                )}

                {results?.movies?.length > 0 && (
                  <SearchGroup label="Movies & TV" icon={<FilmStrip size={11} weight="fill" />}>
                    {results.movies.slice(0, 4).map((m) => (
                      <button
                        key={m.slug}
                        type="button"
                        onClick={() => goTo(`/scenepacks/movie/${m.slug}`)}
                        data-testid={`nav-search-movie-${m.slug}`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition group"
                      >
                        <div className="w-9 h-12 rounded-md bg-cover bg-center border border-white/10 flex-shrink-0 bg-[#232323]"
                          style={{ backgroundImage: bgUrl(m.thumbnail_url, 96) }} />
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-bold text-sm truncate group-hover:text-[#FF8090] transition">{m.movie_title}</div>
                          <div className="text-[11px] text-white/50 truncate">{m.year || "—"} · {m.genre || "—"}</div>
                        </div>
                      </button>
                    ))}
                  </SearchGroup>
                )}

                {results?.scenepacks?.length > 0 && (
                  <SearchGroup label="Characters" icon={<FilmStrip size={11} />}>
                    {results.scenepacks.slice(0, 4).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => goTo(`/scenepacks/movie/${p.movie_slug}`)}
                        data-testid={`nav-search-pack-${p.id}`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition group"
                      >
                        <div className="w-9 h-9 rounded-md bg-cover bg-center border border-white/10 flex-shrink-0 bg-[#232323]"
                          style={{ backgroundImage: bgUrl(p.thumbnail_url, 96) }} />
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-bold text-sm truncate group-hover:text-[#FF8090] transition">{p.title}</div>
                          <div className="text-[11px] text-white/50 truncate">{p.movie_title} · {p.quality}</div>
                        </div>
                      </button>
                    ))}
                  </SearchGroup>
                )}

                {results?.people?.length > 0 && (
                  <SearchGroup label="Actors & celebrities" icon={<Star size={11} weight="fill" />}>
                    {results.people.slice(0, 3).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => goTo(`/people/${p.slug}`)}
                        data-testid={`nav-search-person-${p.slug}`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition group"
                      >
                        <div className="w-9 h-11 rounded-md bg-[#232323] flex items-center justify-center overflow-hidden flex-shrink-0 border border-white/10">
                          {p.photo_url ? <SmartImg src={p.photo_url} width={96} still alt="" className="w-full h-full object-cover object-top" /> : <User size={14} className="text-white/30" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-bold text-sm truncate group-hover:text-[#FF8090] transition">{p.name}</div>
                          <div className="text-[11px] text-white/50 truncate">
                            {p.roles_count} role{p.roles_count !== 1 ? "s" : ""}: {p.roles.slice(0, 3).map((r) => (r.celebrity ? "celebrity packs" : r.character)).join(", ")}
                          </div>
                        </div>
                      </button>
                    ))}
                  </SearchGroup>
                )}

                {results?.creators?.length > 0 && (
                  <SearchGroup label="Creators" icon={<User size={11} weight="fill" />}>
                    {results.creators.slice(0, 4).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => goTo(c.username ? `/creators/@${c.username}` : `/creators/${c.id}`)}
                        data-testid={`nav-search-creator-${c.id}`}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition group"
                      >
                        <div className="w-9 h-9 rounded-full bg-[#880015] flex items-center justify-center text-white text-xs font-bold font-display overflow-hidden flex-shrink-0">
                          {c.avatar ? <SmartImg src={c.avatar} width={96} still alt="" className="w-full h-full object-cover" /> : (c.name?.[0]?.toUpperCase() || "?")}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-bold text-sm truncate group-hover:text-[#FF8090] transition">{c.name}</div>
                          <div className="text-[11px] text-white/50 truncate">
                            {c.username ? `@${c.username} · ` : ""}{c.audios_count} audios · {c.scenepacks_count} scenepacks
                          </div>
                        </div>
                      </button>
                    ))}
                  </SearchGroup>
                )}

                {results && totalResults > 0 && (
                  <button
                    type="button"
                    onClick={() => goTo(`/search?q=${encodeURIComponent(search.trim())}`)}
                    data-testid="nav-search-see-all"
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 border-t border-white/10 hover:bg-white/5 text-xs font-display font-bold uppercase tracking-wider text-[#FF8090] transition"
                  >
                    <span>See all results for &ldquo;{search.trim()}&rdquo;</span>
                    <ArrowRight size={12} weight="bold" />
                  </button>
                )}
              </div>
            )}
          </form>
        </div>

        <div className="flex items-center gap-2">
          {user ? (
            <>
            <NotificationBell />
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenu((m) => !m)}
                aria-expanded={menu}
                data-testid="user-menu-button"
                className="flex items-center gap-2 px-3 py-2 rounded-sm hover:bg-white/5 transition"
              >
                <div className="w-8 h-8 rounded-full bg-[#880015] flex items-center justify-center text-white text-sm font-bold font-display overflow-hidden" data-testid="user-menu-avatar">
                  {user.avatar ? (
                    <SmartImg src={user.avatar} width={96} still alt="" className="w-full h-full object-cover" />
                  ) : (
                    user.name?.[0]?.toUpperCase() || "A"
                  )}
                </div>
                <span className="hidden md:block text-sm font-medium">{user.name}</span>
              </button>
              <div
                className="user-menu-panel absolute right-0 top-full mt-2 w-60 glass rounded-md py-2 shadow-xl overflow-hidden"
                data-open={menu ? "true" : "false"}
                data-testid="user-menu-panel"
                aria-hidden={!menu}
              >
                <div className="px-4 py-2 mb-1 border-b border-white/10">
                  <div className="text-xs text-white/40 font-mono uppercase tracking-wider">
                    {user.role}
                  </div>
                  <div className="text-sm truncate">{user.email}</div>
                </div>
                {[
                  { key: "profile", to: "/profile", icon: <User size={16} />, label: "Profile" },
                  (user.role === "creator" || user.role === "admin") && { key: "dashboard", to: "/dashboard", icon: <UploadSimple size={16} />, label: "Creator Dashboard" },
                  user.role === "admin" && { key: "admin", to: "/admin", icon: <Shield size={16} />, label: "Admin Panel" },
                ].filter(Boolean).map((item, i) => (
                  <Link
                    key={item.key}
                    to={item.to}
                    data-testid={`menu-${item.key}`}
                    tabIndex={menu ? 0 : -1}
                    onClick={() => setMenu(false)}
                    className="user-menu-item"
                    style={{ "--d": menu ? `${70 + i * 55}ms` : "0ms" }}
                  >
                    {item.icon} {item.label}
                  </Link>
                ))}
                <button
                  onClick={() => {
                    setMenu(false);
                    logout();
                  }}
                  data-testid="menu-logout"
                  tabIndex={menu ? 0 : -1}
                  className="user-menu-item"
                  style={{ "--d": menu ? "235ms" : "0ms" }}
                >
                  <SignOut size={16} /> Sign out
                </button>
              </div>
            </div>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="hidden sm:inline-flex font-display font-bold uppercase tracking-wider text-xs px-4 py-2 text-white/80 hover:text-white">
                Sign in
              </Link>
              <Link to="/register" data-testid="nav-register-btn" className="btn-primary !py-2.5 !px-5 !text-xs">
                Join
              </Link>
            </>
          )}
          <button onClick={() => { setFocusMobileSearch(true); setOpen(true); }} aria-label="Search" className="md:hidden w-10 h-10 flex items-center justify-center rounded-full active:bg-white/10" data-testid="nav-mobile-search">
            <MagnifyingGlass size={20} />
          </button>
          <button onClick={() => { setFocusMobileSearch(false); setOpen(true); }} aria-label="Menu" className="lg:hidden w-10 h-10 -mr-1 flex items-center justify-center rounded-full active:bg-white/10" data-testid="nav-mobile-toggle">
            <List size={22} />
          </button>
        </div>
      </div>

      <MobileMenu open={open} focusSearch={focusMobileSearch} onClose={() => { setOpen(false); setFocusMobileSearch(false); }} items={NAV} user={user} logout={logout} />
    <div className="hidden lg:block" onMouseEnter={() => clearTimeout(megaTimer.current)}>
        <AnimatePresence>{mega && <MegaPanel which={mega} />}</AnimatePresence>
      </div>
    </nav>
  );
}

function SearchGroup({ label, icon, children }) {
  return (
    <div className="py-1">
      <div className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090] flex items-center gap-1.5">
        {icon} {label}
      </div>
      {children}
    </div>
  );
}
